import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabase';
import { useEffectiveUser } from './ImpersonationContext';
import { useAnalyzer } from './analyzer/useAnalyzer';
import { isValidSolanaAddress } from './analyzer/analyzerCore';
import {
  ScoreBadge, WinBar, Pnl, Activity, Skel, shortAddr, fmtUsd, fmtHold, verdictLabel, verdictCls,
} from './analyzer/anUi';
import OverviewTab from './analyzer/OverviewTab';
import TokensTab from './analyzer/TokensTab';
import TradesTab from './analyzer/TradesTab';
import StatsExplorerTab from './analyzer/StatsExplorerTab';
import SimulatorTab from './analyzer/SimulatorTab';
import CompareTab from './analyzer/CompareTab';
import TriggersTab from './analyzer/TriggersTab';
import './AnalyzerTab.css';

const TABS = [
  ['overview', "Vue d'ensemble"], ['tokens', 'Tokens'], ['trades', 'Trades'],
  ['explorer', 'Stats Explorer'], ['simulator', 'Simulateur'], ['compare', 'Comparer'], ['triggers', 'Triggers'],
];

export default function AnalyzerTab({ initialAddr, onInitialAddrUsed }) {
  const { effectiveUserId, isImpersonating } = useEffectiveUser();
  const [input, setInput] = useState('');
  const [address, setAddress] = useState('');
  const [runId, setRunId] = useState(0);
  const [inputErr, setInputErr] = useState('');
  const [tab, setTab] = useState('overview');
  const [badges, setBadges] = useState({ scan: null, radar: null, tracked: false });
  const [eurRate, setEurRate] = useState(null);
  const [copied, setCopied] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [keyInput, setKeyInput] = useState('');
  const [keyLoaded, setKeyLoaded] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [keySaved, setKeySaved] = useState(false);
  const [keyErr, setKeyErr] = useState('');
  const { data: d, loading, error } = useAnalyzer(address, runId, apiKey);

  // Arrivée depuis Scan/Radar : pré-remplit seulement (pas d'analyse auto : coût en crédits).
  const usedRef = useRef(onInitialAddrUsed);
  usedRef.current = onInitialAddrUsed;
  useEffect(() => {
    if (initialAddr) { setInput(initialAddr); setInputErr(''); usedRef.current && usedRef.current(); }
  }, [initialAddr]);

  useEffect(() => {
    let cancelled = false;
    fetch('https://api.frankfurter.app/latest?from=USD&to=EUR')
      .then(r => r.json()).then(j => { if (!cancelled && j?.rates?.EUR) setEurRate(j.rates.EUR); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Clé Helius de l'analyseur : Supabase uniquement (profiles.analyzer_helius_key)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = effectiveUserId || session?.user?.id;
        if (!userId) return;
        const { data } = await supabase.from('profiles').select('analyzer_helius_key').eq('id', userId).maybeSingle();
        if (cancelled) return;
        const k = data?.analyzer_helius_key || '';
        setApiKey(k); setKeyInput(k);
      } catch { /* clé absente */ }
      finally { if (!cancelled) setKeyLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [effectiveUserId]);

  const saveKey = async () => {
    if (isImpersonating) return;
    const k = keyInput.trim();
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) return;
    const { error: err } = await supabase.from('profiles').update({ analyzer_helius_key: k || null }).eq('id', userId);
    if (err) { setKeyErr('Échec de la sauvegarde : ' + err.message); return; }
    setKeyErr(''); setApiKey(k); setKeySaved(true); setTimeout(() => setKeySaved(false), 2000);
  };

  // Badges de contexte (lecture seule)
  useEffect(() => {
    if (!address) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = effectiveUserId || session?.user?.id;
        if (!userId) return;
        const [s, r, w] = await Promise.all([
          supabase.from('scan_wallets').select('score').eq('user_id', userId).eq('wallet_address', address).maybeSingle(),
          supabase.from('radar_wallets').select('follow_score').eq('user_id', userId).eq('wallet_address', address).maybeSingle(),
          supabase.from('wallets').select('id').eq('user_id', userId).eq('full_address', address).limit(1),
        ]);
        if (!cancelled) setBadges({ scan: s.data?.score ?? null, radar: r.data?.follow_score ?? null, tracked: !!(w.data && w.data.length) });
      } catch { /* badges facultatifs */ }
    })();
    return () => { cancelled = true; };
  }, [address, effectiveUserId]);

  const run = useCallback((addr) => {
    const v = (addr || '').trim();
    if (!apiKey) { setInputErr('Configurez votre clé Helius.'); return; }
    if (!isValidSolanaAddress(v)) { setInputErr('Adresse Solana invalide (base58, 32 à 44 caractères).'); return; }
    setInputErr(''); setTab('overview'); setBadges({ scan: null, radar: null, tracked: false });
    setAddress(v); setRunId(r => r + 1);
  }, [apiKey]);

  const copy = () => {
    navigator.clipboard.writeText(address).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {});
  };

  const v = d?.verdict;
  return (
    <div className="an-page">
      <div className="an-keybar">
        <span className="an-field-label">🔑 Clé API Helius</span>
        <input className="an-input" type={showKey ? 'text' : 'password'} value={keyInput} autoComplete="off"
          onChange={e => setKeyInput(e.target.value)} placeholder="Clé Helius de l'analyseur" readOnly={isImpersonating} />
        <button className="an-icon-btn" onClick={() => setShowKey(s => !s)} title="Afficher / masquer">{showKey ? '🙈' : '👁'}</button>
        {isImpersonating
          ? <span className="an-muted an-small">Lecture seule en mode impersonation</span>
          : <button className="an-btn an-btn--ghost an-btn--sm" onClick={saveKey}>💾 Sauvegarder</button>}
        {keySaved && <span className="an-saved">✓ Sauvegardé !</span>}
        {keyLoaded && !apiKey && !keySaved && <span className="an-muted an-small">Aucune clé configurée — Configurez votre clé Helius</span>}
        {keyErr && <span className="an-error-inline">{keyErr}</span>}
      </div>
      <div className="an-search">
        <input className="an-input an-input--lg" placeholder="Adresse du wallet Solana" value={input}
          onChange={e => { setInput(e.target.value); setInputErr(''); }}
          onKeyDown={e => e.key === 'Enter' && run(input)} />
        <button className="an-btn an-btn--accent" onClick={() => run(input)} disabled={loading || !keyLoaded}>Analyser</button>
      </div>
      {inputErr && <div className="an-error-inline">{inputErr}</div>}

      {!address && (
        <div className="an-welcome">
          <div className="an-welcome-title">Analyseur de wallet</div>
          <div className="an-welcome-text">Collez une adresse Solana pour obtenir un rapport complet. Chaque analyse est faite à neuf et consomme environ 5 000 crédits Helius. Votre clé est enregistrée dans votre profil Supabase.</div>
        </div>
      )}

      {address && (
        <div className="an-addr-bar">
          <span className="an-id-addr">{shortAddr(address)}</span>
          <button className="an-icon-btn" onClick={copy} title="Copier l'adresse">{copied ? '✓' : '📋'}</button>
          <a className="an-ext" href={`https://solscan.io/account/${address}`} target="_blank" rel="noopener noreferrer">↗</a>
          {badges.scan != null && <span className="an-badge">Score Scan {badges.scan}</span>}
          {badges.radar != null && <span className="an-badge">Score Radar {badges.radar}</span>}
          {badges.tracked && <span className="an-badge an-badge--tracked">Suivi</span>}
          <button className="an-btn an-btn--ghost an-btn--sm an-right" onClick={() => setRunId(r => r + 1)} disabled={loading}>↻ Rafraîchir</button>
        </div>
      )}

      {address && loading && (
        <div className="an-panel an-stack">
          <div className="an-muted">Analyse en cours… (≈ 5 000 crédits Helius, quelques secondes à une minute)</div>
          <Skel h={24} w="50%" /><Skel h={90} /><Skel h={200} />
        </div>
      )}

      {address && !loading && error && (
        <div className="an-panel"><div className="an-error-inline">⚠ {error}</div></div>
      )}

      {d && !loading && (
        <>
          <div className="an-summary">
            <div className="an-summary-top">
              <div><div className="an-summary-label">Score</div><ScoreBadge score={v.score} /></div>
              <div><div className="an-summary-label">Win rate</div>{d.totalTrades > 0 ? <WinBar value={d.winRate} /> : '—'}</div>
              <div><div className="an-summary-label">ROI</div><Pnl value={d.overallRoi} /></div>
              <div><div className="an-summary-label">Meilleur</div>{d.bestTrade ? <Pnl value={d.bestTrade.roi} digits={0} /> : '—'}</div>
              <div><div className="an-summary-label">Pire</div>{d.worstTrade ? <Pnl value={d.worstTrade.roi} digits={0} /> : '—'}</div>
              <div><div className="an-summary-label">Trades fermés</div>{d.totalTrades}</div>
              <div><div className="an-summary-label">Holding moy.</div>{fmtHold(d.dureeMoyMin)}</div>
              <div><div className="an-summary-label">Délai d'entrée moy.</div>{d.avgEntryDelayMin != null ? fmtHold(d.avgEntryDelayMin) : '—'}</div>
              <div><div className="an-summary-label">Volume total</div>{fmtUsd(d.volumeUsd)}</div>
              <div><div className="an-summary-label">Dernière activité</div><Activity ts={d.lastActiveTs} /></div>
            </div>
            <div className="an-summary-bottom">
              <span className={`an-verdict ${verdictCls(v.recommendation)}`}>{verdictLabel(v.recommendation)}</span>
              <div className="an-conf" title={d.confidence.note}>
                <span className="an-summary-label">Confiance : {d.confidence.level}</span>
                <div className="an-bot"><div className="an-bot-fill" style={{ width: `${d.confidence.score}%` }} /></div>
              </div>
            </div>
            {(v.positives.length > 0 || v.warnings.length > 0) && (
              <div className="an-lists">
                <ul className="an-list an-list--pos">{v.positives.map((p, i) => <li key={i}>✓ {p}</li>)}</ul>
                <ul className="an-list an-list--warn">{v.warnings.map((p, i) => <li key={i}>⚠ {p}</li>)}</ul>
              </div>
            )}
          </div>

          <div className="an-tabs">
            {TABS.map(([k, l]) => (
              <button key={k} className={`an-tab${tab === k ? ' an-tab--active' : ''}`} onClick={() => setTab(k)}>{l}</button>
            ))}
          </div>

          {tab === 'overview' && <OverviewTab d={d} eurRate={eurRate} />}
          {tab === 'tokens' && <TokensTab d={d} />}
          {tab === 'trades' && <TradesTab d={d} />}
          {tab === 'explorer' && <StatsExplorerTab d={d} />}
          {tab === 'simulator' && <SimulatorTab d={d} walletName={shortAddr(d.address)} />}
          {tab === 'compare' && <CompareTab a={d} apiKey={apiKey} />}
          {tab === 'triggers' && <TriggersTab address={d.address} walletName={shortAddr(d.address)} />}
        </>
      )}
    </div>
  );
}
